import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, Home, Briefcase, FolderOpen, LogIn, UserPlus, LogOut, User, Users, Network, BookOpen, Search, Settings, HelpCircle, Globe, FileText as FileTextIcon, Briefcase as BriefcaseIcon, ChevronDown, Trash2, MessageSquare, BarChart3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import resumeAPI from '../services/api';
import { messagingAPI } from '../services/messaging';
import { networkAPI } from '../services/network';
import wsClient from '../services/websocket';
import Logo from './Logo';
import InstallAppPrompt from './InstallAppPrompt';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const viewerId = user?.user_id || user?.userId || user?.id || user?._id || null;
  const [profilePicture, setProfilePicture] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [publicLinkId, setPublicLinkId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [invitationCount, setInvitationCount] = useState(0);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      loadProfilePicture();
      loadPublicLinkId();
      if (wsClient.isConfigured()) wsClient.connect();
    } else {
      wsClient.disconnect();
    }
    return () => { wsClient.disconnect(); };
  }, [isAuthenticated]);

  // Listen for profile picture updates from ProfilePage
  useEffect(() => {
    const handleProfilePictureUpdate = (event) => {
      const { photoUrl } = event.detail || {};
      if (photoUrl) {
        setProfilePicture(photoUrl);
        console.log('[Layout] Profile picture updated in navbar:', photoUrl);
      }
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    return () => window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = () => {
      messagingAPI.getUnreadCount().then((n) => setUnreadCount(n)).catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserEmail(user?.email || '');
    } catch {
      setUserEmail('');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !wsClient.isConfigured()) return;
    const handleWs = (data) => {
      if (data?.action === 'new_message') {
        messagingAPI.getUnreadCount().then((n) => setUnreadCount(n)).catch(() => {});
      }
    };
    const unsubscribe = wsClient.onMessage(handleWs);
    return () => {
      unsubscribe?.();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchInvitations = () => {
      networkAPI.getInvitations().then((list) => setInvitationCount(Array.isArray(list) ? list.length : 0)).catch(() => setInvitationCount(0));
    };
    fetchInvitations();
    const t = setInterval(fetchInvitations, 45000);
    return () => clearInterval(t);
  }, [isAuthenticated, location.pathname]);

  const loadPublicLinkId = async () => {
    try {
      // Fetch the user's public_link_id from their profile
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim();
      if (AWS_API_BASE_URL) {
        const getAwsIdToken = () => {
          try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return u?.tokens?.idToken || u?.idToken || '';
          } catch {
            return '';
          }
        };
        const token = getAwsIdToken();
        const awsUrl = (path) => {
          const base = AWS_API_BASE_URL.replace(/\/+$/, '');
          const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
          return `${base}${p}`;
        };
        const res = await fetch(awsUrl('/me/profile'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });
        if (res.ok) {
          const data = await res.json();
          const pid = data?.profile?.public_link_id || null;
          if (pid) setPublicLinkId(pid);
        }
      }
    } catch (e) {
      console.warn('Failed to load public_link_id:', e);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  const loadProfilePicture = async () => {
    try {
      // Load from Users table via /me/profile API (NOT from Resumes table!)
      const AWS_API_BASE_URL_FALLBACK = 'https://uk8q3b8x0a.execute-api.us-east-1.amazonaws.com/dev';
      const AWS_API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL || '').trim() || AWS_API_BASE_URL_FALLBACK;
      
      const getAwsIdToken = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          return u?.tokens?.idToken || u?.idToken || '';
        } catch { return ''; }
      };
      
      const token = getAwsIdToken();
      if (!token) return;
      
      const awsUrl = (path) => {
        const base = AWS_API_BASE_URL.replace(/\/+$/, '');
        const p = String(path || '').startsWith('/') ? String(path || '') : `/${String(path || '')}`;
        return `${base}${p}`;
      };
      
      const res = await fetch(awsUrl('/me/profile'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        const photoUrl = data?.profile?.photo_url || '';
        if (photoUrl) {
          setProfilePicture(photoUrl);
        }
      }
    } catch (error) {
      console.error('Error loading profile picture:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setDropdownOpen(false);
  };

  const handleDeleteAccount = async () => {
    // First confirmation
    const firstConfirm = window.confirm(
      'Are you sure you want to delete your account?\n\n' +
      'This action cannot be undone. All your data including:\n' +
      '• Profile information\n' +
      '• Resumes and portfolios\n' +
      '• Account settings\n\n' +
      'will be permanently deleted.'
    );

    if (!firstConfirm) {
      return;
    }

    // Second confirmation
    const secondConfirm = window.confirm(
      '⚠️ FINAL WARNING ⚠️\n\n' +
      'This is your last chance to cancel.\n\n' +
      'Are you ABSOLUTELY SURE you want to permanently delete your account?\n\n' +
      'Click OK to DELETE PERMANENTLY or Cancel to keep your account.'
    );

    if (!secondConfirm) {
      return;
    }

    try {
      await resumeAPI.deleteAccount();
      alert('Your account has been successfully deleted.');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert(`Failed to delete account: ${error.message}\n\nPlease try again or contact support.`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <nav className="container-custom py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <Logo className="h-10 w-auto" />
            </Link>

            {/* Navigation */}
            <div className="flex items-center space-x-1">
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/') && location.pathname === '/'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Home className="w-5 h-5" />
                <span className="font-medium">Home</span>
              </Link>

              <Link
                to="/learning"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/learning') && location.pathname === '/learning'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                <span className="font-medium">Learning</span>
              </Link>

              <Link
                to="/jobs"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive('/jobs') && location.pathname === '/jobs'
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Search className="w-5 h-5" />
                <span className="font-medium">Jobs</span>
              </Link>

              {isAuthenticated && (
                <>
                  <Link
                    to="/profile"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/profile')
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Network className="w-5 h-5" />
                    <span className="font-medium">Profile</span>
                  </Link>
                  {userEmail === 'benjamin.maderajr@gmail.com' && (
                    <Link
                      to="/platform-analytics"
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        isActive('/platform-analytics')
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <BarChart3 className="w-5 h-5" />
                      <span className="font-medium">Platform Analytics</span>
                    </Link>
                  )}
                  <Link
                    to="/builder"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/builder')
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FileText className="w-5 h-5" />
                    <span className="font-medium">Resume</span>
                  </Link>
                  <Link
                    to="/portfolio"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/portfolio')
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Briefcase className="w-5 h-5" />
                    <span className="font-medium">Portfolio</span>
                  </Link>
                  <Link
                    to="/my-resumes"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive('/my-resumes')
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FolderOpen className="w-5 h-5" />
                    <span className="font-medium">My Resumes</span>
                  </Link>
                  <Link
                    to="/my-network"
                    className={`flex flex-col items-center space-y-0.5 px-4 py-2 rounded-lg transition-colors relative ${
                      isActive('/my-network')
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Users className="w-5 h-5" />
                    <span className="text-xs font-medium">My Network</span>
                    {invitationCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary-600 text-white text-xs font-medium">
                        {invitationCount > 99 ? '99+' : invitationCount}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {/* Auth Buttons */}
              <div className="ml-4 flex items-center space-x-2 border-l border-gray-200 pl-4">
                {isAuthenticated ? (
                  <div className="relative" ref={dropdownRef}>
                    {/* Profile Picture with "Me" - Clickable */}
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="flex flex-col items-center space-y-1 px-2 py-1 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="relative">
                        {profilePicture ? (
                          <img
                            src={profilePicture}
                            alt="Profile"
                            className="w-8 h-8 rounded-full object-cover border-2 border-gray-300"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center border-2 border-gray-300">
                            <User className="w-5 h-5 text-primary-600" />
                          </div>
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-700">Me</span>
                      <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {dropdownOpen && (
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                        {/* View profile button - uses public_link_id for global access */}
                        <Link
                          to={publicLinkId ? `/profile/view?public=${encodeURIComponent(publicLinkId)}` : '/profile/view'}
                          onClick={() => setDropdownOpen(false)}
                          className="block w-full px-4 py-3 bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors text-center"
                        >
                          View profile
                        </Link>
                        <Link
                          to="/messaging"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors relative"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-sm">Messaging</span>
                          {unreadCount > 0 && (
                            <span className="absolute right-3 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary-600 text-white text-xs font-medium">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </Link>

                        {/* Account Section */}
                        <div className="border-t border-gray-200">
                          <div className="px-4 py-2 bg-gray-50">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Account</h3>
                          </div>
                          <Link
                            to="/settings"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="text-sm">Settings & Privacy</span>
                          </Link>
                          <Link
                            to="/help"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <HelpCircle className="w-4 h-4" />
                            <span className="text-sm">Help</span>
                          </Link>
                          <Link
                            to="/language"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Globe className="w-4 h-4" />
                            <span className="text-sm">Language</span>
                          </Link>
                          {/* Delete Account - Only available on localhost/dev server */}
                          {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
                            <button
                              onClick={() => {
                                setDropdownOpen(false);
                                handleDeleteAccount();
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors text-left"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="text-sm font-medium">Delete Account (Dev Only)</span>
                            </button>
                          )}
                        </div>

                        {/* Manage Section */}
                        <div className="border-t border-gray-200">
                          <div className="px-4 py-2 bg-gray-50">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Manage</h3>
                          </div>
                          <Link
                            to="/posts-activity"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <FileTextIcon className="w-4 h-4" />
                            <span className="text-sm">Posts & Activity</span>
                          </Link>
                          <Link
                            to="/job-posting-account"
                            onClick={() => setDropdownOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <BriefcaseIcon className="w-4 h-4" />
                            <span className="text-sm">Job Posting Account</span>
                          </Link>
                        </div>

                        {/* Sign Out */}
                        <div className="border-t border-gray-200">
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-left"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Sign Out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <LogIn className="w-5 h-5" />
                      <span className="font-medium">Login</span>
                    </Link>
                    <Link
                      to="/register"
                      className="btn btn-primary inline-flex items-center space-x-2"
                    >
                      <UserPlus className="w-5 h-5" />
                      <span>Sign Up</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Install App Prompt */}
      <InstallAppPrompt />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8 mt-auto">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Career Branding Network</h3>
              <p className="text-sm">
                Your complete professional platform: Build your networking profile, create stunning resumes, and showcase your portfolio.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/profile" className="hover:text-white transition-colors">
                    Professional Networking Profile
                  </Link>
                </li>
                <li>
                  <Link to="/builder" className="hover:text-white transition-colors">
                    Resume Builder & Hosting
                  </Link>
                </li>
                <li>
                  <Link to="/portfolio" className="hover:text-white transition-colors">
                    Portfolio
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <p className="text-sm">
                Career Branding Network
                <br />
                Powered by Claude and Cursor
                <br />
                Benjamin Madera CBN LLC
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} Career Branding Network. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
